/**
 * File: HelpDialog.tsx
 * Path: apps/admin/src/modules/layout/components/HelpDialog.tsx
 * Description: What the ? in a page header opens: that page's tutorials,
 * grouped, each with its step count and "Show me". A search box looks
 * across every tutorial in the admin. A tutorial runs alone as a spotlight
 * tour. Fills the screen on a phone.
 */

import React from "react";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/shared/components/Dialog";
import { fieldClass } from "@/shared/components/LeadPrimitive";
import { labelTextClass } from "@/shared/components/TextPrimitive";
import { useHelpDialogController } from "@/modules/layout/hooks/useHelpDialogController";

export const HelpDialog: React.FC = () => {
  const h = useHelpDialogController();
  return (
    <Dialog open={h.open} onOpenChange={h.onOpenChange}>
      <DialogContent className="flex h-dvh max-h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-stack-md overflow-hidden rounded-none p-gutter-md max-sm:left-0 max-sm:top-0 sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[85vh] sm:max-w-xl sm:translate-x-[-50%] sm:translate-y-[-50%]">
        <DialogHeader className="space-y-stack-xs text-left">
          <DialogTitle className="pr-gutter-lg text-lg font-extrabold">Help: {h.pageTitle}</DialogTitle>
          <DialogDescription className="text-sm">
            Pick a job. The admin shows you where to click, one step at a time.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-stack-sm top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="search"
            aria-label="Search tutorials"
            placeholder="Search every tutorial"
            className={`${fieldClass} pl-gutter-lg`}
            value={h.query}
            onChange={(e) => h.setQuery(e.target.value)}
          />
        </div>
        <div className="min-h-0 flex-1 space-y-stack-md overflow-y-auto" role="region" aria-label="Tutorials" tabIndex={0}>
          {h.groups.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {h.searching
                ? "No tutorial matches that. Try one word, like invoice or lead."
                : "This page has no tutorial yet. Search above for one from another page."}
            </p>
          )}
          {h.groups.map(([group, tutorials]) => (
            <section key={group} aria-label={group} className="space-y-stack-xs">
              <h3 className={labelTextClass}>{group}</h3>
              <ul className="divide-y divide-border/50 border border-border/50">
                {tutorials.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-stack-md p-gutter-sm">
                    <span className="min-w-0">
                      <span className="block text-base font-semibold">{t.title}</span>
                      <span className="block text-sm text-muted-foreground">{t.hint}</span>
                    </span>
                    <button
                      type="button"
                      aria-label={`Show me: ${t.title}`}
                      onClick={() => h.run(t)}
                      className="min-h-11 shrink-0 border border-border/60 px-gutter-sm text-sm font-bold hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      Show me →
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <div className="border-t border-border/50 pt-stack-sm">
          <button
            type="button"
            onClick={h.seeEvery}
            className="min-h-11 text-sm font-bold text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            See every tutorial
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
